import { useState } from "react";

import { AxiosError } from "axios";
import moment from "moment";

import {
  getUserFacingApiErrorMessage,
  isFeatureActiveOnCurrentFarm,
  useBulkUpdateCropDates,
  useBulkUpdateCropSeedingPractice,
  useBulkUpdateCropYield,
  useBulkUpdateMineralFertilizerPractice,
  useBulkUpdateOrganicFertilizerPractice,
  useBulkUpdateTillagePractice,
  useTranslation,
} from "utils";

import { FEATURE_FLAG_SLUGS, HARVEST_UNIT_VALUE, HarvestUnitValue } from "consts";

import {
  BulkUpdateCropYieldRequestBody,
  BulkUpdateMineralFertilizerPracticesRequestBody,
  BulkUpdateOrganicFertilizerPracticesRequestBody,
  BulkUpdateSeedingPracticesRequestBody,
  BulkUpdateTillagePracticesRequestBody,
  PRACTICE_SLUG,
  PracticeFCPO,
  PracticeFCPORequest,
  ZERO_UUID,
} from "models";

import { Message as message } from "components";

import { FormPracticeObject } from "pages/app/all-field-view/components/AddEditCropDrawer.tsx";
import { CROP } from "pages/app/all-field-view/constants/cropForm.ts";
import { createCropPracticeRequestBody } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import {
  type MissingDataDrawerPracticeType,
  type MissingDataForm,
  YIELD_PSEUDO_PRACTICE_TYPE,
} from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import {
  BULK_DATA_DRAWER_MODE,
  type BulkDataDrawerMode,
  HAS_ITEM_VALUE,
  getItemPractices,
  getSharedEntryGroups,
  isItemWritable,
  isMultiValueDataPoint,
  toPracticeRequest,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

type BulkPracticeSaveParams = {
  missingDataFormItems: MissingDataForm[];
  // "add" targets only the crops missing a value for the data point, "edit" targets every editable one
  mode: BulkDataDrawerMode;
  // the practices the form is showing, carried into the request so a save doesn't drop the entries it didn't render
  existingPracticesInForm: PracticeFCPO[];
};

// bulk-update-yield and bulk-update-crop-dates report back which crops they silently skipped instead of failing.
// They are handed to the caller rather than acted on here, so it can order them against its own success handling
export type BulkPracticeSaveResult = {
  didSave: boolean;
  skippedCropIds: string[];
};

// one bulk practice request: the crops that end up with the same list of entries
type PracticeRequestForCrops = {
  farmFieldCropIds: string[];
  practices: PracticeFCPORequest[];
};

// Per-practice bulk save shared by the bulk drawer. AddEditMissingDataDrawer still carries its own copy of this
// logic for the single-cell flow — fold it into this hook once the bulk flow replaces it everywhere.
export const useBulkPracticeSave = ({ missingDataFormItems, existingPracticesInForm, mode }: BulkPracticeSaveParams) => {
  const { t } = useTranslation("historicalPracticeDataTableView");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const isSoilPlus3DataGapFeatureActive = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP);

  const fieldIds = missingDataFormItems.map((item) => item.fieldId);
  const { mutateAsync: updateCropDates } = useBulkUpdateCropDates({ fieldIds });
  const { mutateAsync: updateCropYield } = useBulkUpdateCropYield({ fieldIds });
  const { mutateAsync: updateSeeding } = useBulkUpdateCropSeedingPractice({ fieldIds });
  const { mutateAsync: updateTillage } = useBulkUpdateTillagePractice({ fieldIds });
  const { mutateAsync: updateOrganic } = useBulkUpdateOrganicFertilizerPractice({ fieldIds });
  const { mutateAsync: updateMineral } = useBulkUpdateMineralFertilizerPractice({ fieldIds });

  const writableCropIdsFor = (practiceType: MissingDataDrawerPracticeType): string[] =>
    missingDataFormItems.filter((item) => isItemWritable(item, practiceType, mode)).map((item) => item.key);

  // The bulk practice endpoints replace a crop's whole list with the one sent, so each crop gets its own entries back
  // minus the shared ones the form stood in for, plus what the form holds. Crops that end up with the same list share
  // one request. An intentional "no" isn't added on top of anything: it goes only to the crops that it describes
  const getPracticeRequestsByCrops = (practiceType: MissingDataDrawerPracticeType, formPractices: PracticeFCPORequest[]): PracticeRequestForCrops[] => {
    const isAnsweredNo = formPractices.length > 0 && formPractices.every((practice) => practice.assignedPracticeId === ZERO_UUID);
    const sharedEntryGroups = getSharedEntryGroups(missingDataFormItems, practiceType, mode);
    const groupByEntryId = new Map(sharedEntryGroups.filter((group) => group.entry.id).map((group) => [group.entry.id, group]));
    const writableItems = missingDataFormItems.filter(
      (item) => isItemWritable(item, practiceType, mode) && !(isAnsweredNo && mode === BULK_DATA_DRAWER_MODE.add && HAS_ITEM_VALUE[practiceType]?.(item))
    );

    const requestsByPractices = writableItems.reduce((requests, item) => {
      const shownRows = sharedEntryGroups.map((group) => group.rowsByCropKey[item.key]).filter((row) => row !== undefined);
      const keptEntries = isAnsweredNo ? [] : getItemPractices(item, practiceType).filter((entry) => !shownRows.includes(entry));
      const formPracticesOfCrop = formPractices.filter((practice) => {
        const practiceId = (practice as { id?: string }).id;
        const group = practiceId ? groupByEntryId.get(practiceId) : undefined;
        return !group || item.key in group.rowsByCropKey;
      });
      const practices: PracticeFCPORequest[] = [
        // a "no" left over from before stops being true once there is an actual entry
        ...keptEntries.filter((entry) => formPracticesOfCrop.length === 0 || entry.assignedPracticeId !== ZERO_UUID).map(toPracticeRequest),
        ...formPracticesOfCrop,
      ];
      const requestKey = JSON.stringify(practices);
      const request = requests.get(requestKey) ?? { farmFieldCropIds: [], practices };
      return requests.set(requestKey, { ...request, farmFieldCropIds: [...request.farmFieldCropIds, item.key] });
    }, new Map<string, PracticeRequestForCrops>());

    return Array.from(requestsByPractices.values());
  };

  const warnAboutSkippedCrops = (skippedCropIds: string[], totalCount: number) => {
    if (skippedCropIds.length === 0) return;
    message({ content: t("drawer.skippedCropsWarning", { updatedCount: totalCount - skippedCropIds.length, total: totalCount }) });
  };

  // reports whether the save went through, so the drawer can stay open on a validation-shaped no-op
  const save = async (practiceType: MissingDataDrawerPracticeType, formValues: Record<string, unknown>): Promise<BulkPracticeSaveResult> => {
    const crop = missingDataFormItems[0]?.crop;
    if (!crop) return { didSave: false, skippedCropIds: [] };

    const farmFieldCropIds = writableCropIdsFor(practiceType);
    setIsSaving(true);

    try {
      if (practiceType === PRACTICE_SLUG.CROPS) {
        const seedingDateRange = formValues.seedingDateRange as [moment.Moment, moment.Moment] | undefined;
        const harvestDateRange = formValues.harvestDateRange as [moment.Moment, moment.Moment] | undefined;
        if (!seedingDateRange?.[0] || !seedingDateRange?.[1] || !harvestDateRange?.[0] || !harvestDateRange?.[1]) return { didSave: false, skippedCropIds: [] };

        const response = await updateCropDates(
          farmFieldCropIds.map((fieldCropId) => ({
            fieldCropId,
            seedingStart: seedingDateRange[0].toISOString(),
            seedingEnd: seedingDateRange[1].toISOString(),
            harvestStart: harvestDateRange[0].toISOString(),
            harvestEnd: harvestDateRange[1].toISOString(),
          }))
        );
        if (!response.success) return { didSave: false, skippedCropIds: [] };
        warnAboutSkippedCrops(response.skippedCropIds, farmFieldCropIds.length);
        return { didSave: true, skippedCropIds: response.skippedCropIds };
      }

      if (practiceType === YIELD_PSEUDO_PRACTICE_TYPE) {
        const cropFormValues = (formValues[CROP] ?? {}) as Record<string, unknown>;
        const request: BulkUpdateCropYieldRequestBody[] = farmFieldCropIds.map((farmFieldCropId) => ({
          farmFieldCropId,
          yieldPerHa: (cropFormValues.yieldPerHa as number | null) ?? null,
          yieldPerHaUnit: (cropFormValues.yieldPerHaUnit as HarvestUnitValue | undefined) ?? HARVEST_UNIT_VALUE.tonnesPerHa,
          sanityNoteYield: (cropFormValues.sanityNoteYield as string | null) ?? null,
        }));
        const response = await updateCropYield(request);
        if (!response.success) return { didSave: false, skippedCropIds: [] };
        warnAboutSkippedCrops(response.skippedCropIds, farmFieldCropIds.length);
        return { didSave: true, skippedCropIds: response.skippedCropIds };
      }

      if (practiceType === PRACTICE_SLUG.SEEDINGS) {
        const seedingPracticeType = formValues.seedingPracticeType as string | undefined;
        // null when the farmer hasn't picked a type, or when the selection holds several different ones — nothing
        // has changed either way, so there is nothing to send
        if (!seedingPracticeType) return { didSave: true, skippedCropIds: [] };

        const request: BulkUpdateSeedingPracticesRequestBody = { farmFieldCropIds, seedingPracticeType };
        const response = await updateSeeding(request);
        return { didSave: response.success, skippedCropIds: [] };
      }

      const { isTillageApplied, isMineralApplied, isMineralActivated, isOrganicApplied, hasFertilizerManagement, ...formPracticeObject } = formValues as {
        isTillageApplied?: boolean | null;
        isMineralApplied?: boolean | null;
        isMineralActivated?: boolean | null;
        isOrganicApplied?: boolean | null;
        hasFertilizerManagement?: boolean | null;
      };
      const formPractices = Object.values(formPracticeObject as FormPracticeObject).map((practiceData) => {
        const { dateRange, ...rest } = practiceData;

        return {
          ...rest,
          startDate: (dateRange as moment.Moment[])?.[0]?.toISOString(),
          endDate: (dateRange as moment.Moment[])?.[1]?.toISOString(),
        } as PracticeFCPO;
      });

      const request = createCropPracticeRequestBody(
        crop,
        formPractices,
        isTillageApplied ?? null,
        isMineralApplied ?? null,
        isMineralActivated ?? null,
        isOrganicApplied ?? null,
        hasFertilizerManagement ?? null,
        existingPracticesInForm,
        isSoilPlus3DataGapFeatureActive
      );

      if (!isMultiValueDataPoint(practiceType)) return { didSave: false, skippedCropIds: [] };
      const updatePractices = (practices: PracticeFCPORequest[], cropIds: string[]) => {
        if (practiceType === PRACTICE_SLUG.TILLAGES) return updateTillage({ farmFieldCropIds: cropIds, practices } as BulkUpdateTillagePracticesRequestBody);
        if (practiceType === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
          return updateOrganic({ farmFieldCropIds: cropIds, practices } as BulkUpdateOrganicFertilizerPracticesRequestBody);
        }
        return updateMineral({ farmFieldCropIds: cropIds, practices } as BulkUpdateMineralFertilizerPracticesRequestBody);
      };

      const formPracticesOfType = request.practices.filter((practice) => practice.type === practiceType);
      const responses = await Promise.all(
        getPracticeRequestsByCrops(practiceType, formPracticesOfType).map(({ practices, farmFieldCropIds: cropIds }) => updatePractices(practices, cropIds))
      );
      return { didSave: responses.every((response) => response.success), skippedCropIds: [] };
    } catch (error) {
      const errorMessage = error instanceof AxiosError ? getUserFacingApiErrorMessage(error) : error instanceof Error ? error.message : String(error);
      message({ content: `${errorMessage}`, icon: "error" });
      return { didSave: false, skippedCropIds: [] };
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};
